<template>
  <div class="debito-view">
    <h5 class="mb-1">
      {{ grupoDebito }}
    </h5>

    <div v-if="itens && itens.length">
      <!-- HISTÓRICO: múltiplas tabelas por período -->
      <div v-if="modoHistorico && gruposPorPeriodo.length">
        <!-- Primeira tabela com cabeçalho -->
        <b-table
          v-if="gruposPorPeriodo[0]"
          :items="tabelaPorPeriodo(gruposPorPeriodo[0])"
          :fields="campos"
          small
          bordered
          responsive
          head-variant="light"
          :thead-class="modoHistorico ? 'cabecalho-debitos-mit' : undefined"
          class="debito-table mb-3"
        >
          <!-- Slot simples (opcional) -->
          <template #cell(valorApurado)="{ value }">
            {{ value }}
          </template>
        </b-table>

        <!-- Demais tabelas, sem cabeçalho -->
        <div
          v-for="grupo in gruposPorPeriodo.slice(1)"
          :key="grupo.periodo"
          class="tabela-periodo"
        >
          <b-table
            :items="tabelaPorPeriodo(grupo)"
            :fields="campos"
            small
            bordered
            responsive
            :show-empty="false"
            thead-class="cabecalho-oculto"
            class="debito-table"
          >
            <template #cell(valorApurado)="{ value }">
              {{ value }}
            </template>
          </b-table>
        </div>

        <div class="text-right mt-3 total-geral">
          <strong>Total:</strong> {{ formatarMoeda(total) }}
        </div>
      </div>

      <!-- MODO NORMAL: tabela única -->
      <div v-else>
        <b-table
          :items="tabela"
          :fields="campos"
          small
          bordered
          responsive
          head-variant="light"
          class="debito-table"
        />
        <div class="text-right mt-1">
          <strong>Total:</strong> {{ formatarMoeda(total) }}
        </div>
      </div>
    </div>

    <div
      v-else
      class="text-muted"
    >
      Sem débitos para este grupo.
    </div>
  </div>
</template>

<script>
export default {
  name: 'VisualizacaoMitApuracaoDebitos',
  props: {
    grupoDebito: { type: String, required: true },
    itens: { type: Array, default: () => [] },
    periodoApuracao: { type: String, default: '' },
    modoHistorico: { type: Boolean, default: false },
  },
  computed: {
    campos() {
      const colunas = [
        {
          key: 'codigoReceita',
          label: 'Código Receita',
          formatter: value => this.formatarCodigoReceita(value),
          // Aplicar classe somente quando no modo histórico
          tdClass: this.modoHistorico ? 'col-codigo-receita' : undefined,
        },
        {
          key: 'periodoApuracao',
          label: 'Período Apuração',
          // Aplicar classe somente quando no modo histórico
          tdClass: this.modoHistorico ? 'col-periodo-apuracao' : undefined,
        },
        {
          key: 'valor',
          label: 'Total Valor do Débito',
          // Aplicar classe somente quando no modo histórico
          tdClass: this.modoHistorico ? 'col-total-valor-debito' : undefined,
        },
      ]

      if (this.modoHistorico) {
        colunas.push({
          key: 'valorApurado',
          label: 'Total Débito do P.A.',
          thClass: '',
          // CLASSES aplicadas diretamente no <td> desta coluna
          tdClass: (value, key, item) => this.getTdClassValorApurado(item),
        })
      }

      return colunas
    },

    gruposPorPeriodo() {
      if (!this.modoHistorico || !this.itens?.length) return []

      const grupos = {}
      this.itens.forEach(item => {
        const periodo = item?.periodoApuracao || this.periodoApuracao || 'Sem período'
        if (!grupos[periodo]) grupos[periodo] = { periodo, itens: [], total: 0 }
        grupos[periodo].itens.push(item)
        grupos[periodo].total += this.getValorDebito(item)
      })

      // Mais recente primeiro
      return Object.values(grupos).sort((a, b) => {
        const A = a.periodo.replace(/[^\d]/g, '')
        const B = b.periodo.replace(/[^\d]/g, '')
        return A && B ? B.localeCompare(A) : b.periodo.localeCompare(a.periodo)
      })
    },

    tabelaPorPeriodo() {
      return grupo => grupo.itens.map((item, idx) => {
        const isFirst = idx === 0
        const isLast = idx === grupo.itens.length - 1
        const multi = grupo.itens.length > 1

        return {
          codigoReceita: item?.CodigoDebito ?? item?.codigoDebito ?? '',
          periodoApuracao: item?.periodoApuracao || this.periodoApuracao || '',
          valor: this.formatarMoeda(this.getValorDebito(item)),
          valorApurado: isLast ? this.formatarMoeda(grupo.total) : '',
          isFirstRow: isFirst,
          isLastRow: isLast,
          hasGroup: multi,
        }
      })
    },

    tabela() {
      if (this.modoHistorico) return []
      return (this.itens || []).map(d => ({
        codigoReceita: d?.CodigoDebito ?? d?.codigoDebito ?? '',
        periodoApuracao: d?.periodoApuracao || this.periodoApuracao || '',
        valor: this.formatarMoeda(this.getValorDebito(d)),
      }))
    },

    total() {
      return (this.itens || []).reduce((acc, d) => acc + this.getValorDebito(d), 0)
    },
  },

  methods: {
    getValorDebito(d) {
      const raw = d?.ValorDebito ?? d?.valorDebito ?? d?.valor ?? 0
      const num = Number(raw)
      return Number.isFinite(num) ? num : 0
    },

    formatarMoeda(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },

    // Formata código de receita: mantém os 2 últimos dígitos e insere traço antes deles
    // Exemplo: "555555" → "5555-55"
    formatarCodigoReceita(value) {
      if (!value || typeof value !== 'string') return value || ''

      // Remove qualquer traço existente e espaços
      const valorLimpo = value.toString().replace(/[^\d]/g, '')

      // Se tem menos de 3 dígitos, retorna como está
      if (valorLimpo.length <= 2) return value

      // Separa os dígitos, mantendo os 2 últimos
      const parteInicial = valorLimpo.slice(0, -2)
      const doisUltimos = valorLimpo.slice(-2)

      // Retorna formatado com traço
      return `${parteInicial}-${doisUltimos}`
    },

    // Decide classes do <td> da coluna "Total Débito do P.A."
    getTdClassValorApurado(item) {
      // Sempre alinhar à direita
      const base = 'col-valor-apurado text-right'
      // Sem agrupamento: mantém bordas padrão
      if (!item || !item.hasGroup) return base

      // Com agrupamento: remove bordas verticais sempre;
      // só primeira linha tem borda-top; só última tem borda-bottom.
      // As classes abaixo serão tratadas no SCSS.
      if (item.isFirstRow && item.isLastRow) {
        // Grupo de 1 linha: mantém bordas top e bottom
        return `${base} col-pa--group col-pa--top col-pa--bottom`
      }
      if (item.isFirstRow) return `${base} col-pa--group col-pa--top`
      if (item.isLastRow) return `${base} col-pa--group col-pa--bottom`
      // Linhas intermediárias (nem primeira nem última)
      return `${base} col-pa--group col-pa--mid`
    },
  },
}
</script>
